import { LngLatBounds, Marker } from 'maplibre-gl';
import ServiceProvider from '../markers/ServiceProvider';
import SPExistingServiceJobMarker from '../markers/SPExistingServiceJob';
import ServiceJobMarker from '../markers/ServiceJob';

class MapLib {
  constructor({
    map,
    zoom,
    speed,
    theme,
    onServiceProviderClick,
    isAssignedOrAcceptedSP,
  }) {
    this.map = map;
    this.unHighlightedMarkerOpacity = 0.5;
    this.highlightedMarkerOpacity = 1;
    this.zoom = zoom;
    this.speed = speed;
    this.theme = theme;
    this.marker = {
      job: null,
      serviceProviders: [],
      selectedServiceProvider: null,
    };
    this.onServiceProviderClick = onServiceProviderClick;
    this.isAssignedOrAcceptedSP = isAssignedOrAcceptedSP;
    this.serviceProviders = [];
  }

  jumpTo(lat, long) {
    let centerTo = { speed: this.speed, zoom: this.zoom, essential: true };
    centerTo = {
      ...centerTo,
      center: [long, lat],
    };
    this.map.jumpTo(centerTo);
  }

  flyTo(lat, long) {
    let centerTo = {
      speed: 4,
      zoom: 15,
      essential: true,
    };
    centerTo = {
      ...centerTo,
      center: [long, lat],
    };
    this.map.flyTo(centerTo);
  }

  static coreCreateLayer({
    layerId,
    sourceName,
    markerName,
    layoutProps = {},
    paintProps = {},
  }) {
    return {
      id: layerId,
      type: 'symbol',
      source: sourceName,
      layout: {
        'icon-image': markerName,
        ...layoutProps,
      },
      paint: {
        ...paintProps,
      },
    };
  }

  coreShowLayer({ name }) {
    this.map.setLayoutProperty(name, 'visibility', 'visible');
  }

  coreHideLayer({ name }) {
    this.map.setLayoutProperty(name, 'visibility', 'none');
  }

  static coreCreateSource({ features }) {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    };
  }

  static coreSvgObjectToImageSrc(svgObj) {
    const xmlSerializer = new XMLSerializer();
    const svgString = xmlSerializer.serializeToString(svgObj);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  }

  static getCenter(p1, p2) {
    const [lng1, lat1] = p1;
    const [lng2, lat2] = p2;

    return [(lng1 + lng2) / 2, (lat1 + lat2) / 2];
  }

  coreFitToBounds({ home, coordinates }) {
    const bounds = new LngLatBounds();
    const globalPadding = { top: 100, bottom: 100, left: 500, right: 600 };
    this.map.setPadding(globalPadding);
    coordinates.forEach(async c => {
      bounds.extend([c[0], c[1]]);
    });
    // Find the farthest edge from home
    const westSpan = home[0] - bounds.getWest();
    const eastSpan = bounds.getEast() - home[0];
    const southSpan = home[1] - bounds.getSouth();
    const northSpan = bounds.getNorth() - home[1];

    const lngSpan = Math.max(westSpan, eastSpan);
    const latSpan = Math.max(southSpan, northSpan);

    // Create symmetric bounds around home
    const centeredBounds = new LngLatBounds(
      [home[0] - lngSpan, home[1] - latSpan],
      [home[0] + lngSpan, home[1] + latSpan],
    );

    this.map.fitBounds(centeredBounds);
  }

  addTestMarkers({ markerCoordinates }) {
    markerCoordinates.forEach(({ lng, lat }, index) => {
      new Marker().setLngLat([lng, lat]).addTo(this.map);
      // eslint-disable-next-line no-console
      console.log(`Marker ${index} added at`, { lng, lat });
    });
  }

  setJob({ job }) {
    this.job = job;
  }

  setServiceProviders({ serviceProviders }) {
    this.cleanSp();
    this.serviceProviders = serviceProviders;
  }

  renderJob({ setActiveTab }) {
    this.serviceJobApplyToMapAsMarker({ setActiveTab });
  }

  renderServiceProviders() {
    if (this.serviceProviders.length > 0) {
      this.serviceProviderApplyToMapAsMarkers();
    }
  }

  removeAllServiceProviderMarkers() {
    this.marker?.serviceProviders?.forEach(marker => {
      if (marker) {
        marker.remove();
      }
    });
    this.marker.serviceProviders = [];
  }

  cleanSp() {
    this.removeAllServiceProviderMarkers();
    this.serviceProviders = [];
    this.marker = {
      job: null,
      serviceProviders: [],
      selectedServiceProvider: null,
    };
  }

  async adjustMap() {
    if (this.job.locationLng && this.job.locationLat) {
      const home = [this.job.locationLng, this.job.locationLat];
      const coordinates = [];
      if (
        Array.isArray(this.serviceProviders) &&
        this.serviceProviders?.length > 0
      ) {
        await this.serviceProviders.forEach(async sp => {
          sp?.locations.forEach(async location => {
            coordinates.push([location?.address?.lng, location.address?.lat]);
          });
        });
      }

      if (coordinates?.length > 0) {
        this.coreFitToBounds({
          home,
          coordinates,
        });
      } else {
        this.map.flyTo({
          center: home,
        });
      }
    }
  }

  async adjustMapForLocation({ location }) {
    const home = location;
    const center = MapLib.getCenter(location, home);
    const job = [this.job.locationLng, this.job.locationLat];
    const coordinates = [job];
    this.coreFitToBounds({
      home: center,
      coordinates,
    });
  }

  hideAssignedRoute() {
    const layer = 'LAYER_ASSIGNED_ACCEPTED_ROUTE';
    if (this.map.getLayer(layer)) {
      this.map.setLayoutProperty(layer, 'visibility', 'none');
    }
  }

  showAssignedRoute() {
    const layer = 'LAYER_ASSIGNED_ACCEPTED_ROUTE';
    if (this.map.getLayer(layer)) {
      this.map.setLayoutProperty(layer, 'visibility', 'visible');
    }
  }

  removeAcceptedAssignedSPSJRoute() {
    const source = 'SOURCE_ASSIGNED_ACCEPTED_ROUTE';
    const layer = 'LAYER_ASSIGNED_ACCEPTED_ROUTE';
    if (this.map.getLayer(layer)) {
      this.map.removeLayer(layer);
    }
    if (this.map.getSource(source)) {
      this.map.removeSource(source);
    }
  }

  setAcceptedAssignedSPSJRoute({ route }) {
    const source = 'SOURCE_ASSIGNED_ACCEPTED_ROUTE';
    const layer = 'LAYER_ASSIGNED_ACCEPTED_ROUTE';
    if (route) {
      this.removeAcceptedAssignedSPSJRoute();
      const routeGeoJson = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: route,
        },
      };

      this.map.addSource(source, {
        type: 'geojson',
        data: routeGeoJson,
      });

      this.map.addLayer({
        id: layer,
        type: 'line',
        source,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#1976d2',
          'line-width': 5,
        },
      });
    }
  }

  serviceJobApplyToMapAsMarker({ setActiveTab }) {
    const svgMarker = ServiceJobMarker({
      onClick: () => {
        setActiveTab(0);
      },
    });
    svgMarker.style.cursor = 'pointer';
    svgMarker.style.index = 1000;

    this.marker.job = new Marker({ element: svgMarker, anchor: 'bottom' })
      .setLngLat([this.job.locationLng, this.job.locationLat])
      .addTo(this.map);
  }

  static serviceProviderGetMarkerId(serviceProviderId, locationId) {
    return `SP_${serviceProviderId}-${locationId}`;
  }

  static serviceProviderToggleMarker(serviceProviderId, locations, show) {
    locations.forEach(location => {
      if (!location?.id) return;
      const marker = document.getElementById(
        MapLib.serviceProviderGetMarkerId(serviceProviderId, location.id),
      );
      if (marker) {
        marker.style.display = show ? '' : 'none';
      }
    });
  }

  serviceProviderServiceJobToggleMarker(checked, index) {
    const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);
    if (checked) {
      this.coreShowLayer({ name: layerName });
    } else {
      this.coreHideLayer({ name: layerName });
    }
  }

  hideAllServiceProviderMarkers() {
    if (!this.serviceProviders) return;

    this.serviceProviders.forEach(sp => {
      sp.locations.forEach(location => {
        const marker = document.getElementById(
          MapLib.serviceProviderGetMarkerId(sp.id, location.id),
        );
        if (marker) {
          marker.style.display = 'none';
        }
      });
    });

    this.serviceProviders.forEach((sp, index) => {
      const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);
      this.coreHideLayer({ name: layerName });
    });
  }

  static pulseAnimationToMarker({ action = 'remove', element }) {
    // Remove existing animations
    element.querySelectorAll('animate').forEach(anim => anim.remove());
    if (action === 'remove') {
      element.setAttribute('display', 'none');
      return;
    }
    // Fade out and back in
    const animateOpacity = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'animate',
    );
    animateOpacity.setAttribute('attributeName', 'stroke-opacity');
    animateOpacity.setAttribute('values', '0;0.7;0');
    animateOpacity.setAttribute('dur', '2.5s');
    animateOpacity.setAttribute('repeatCount', 'indefinite');
    element.appendChild(animateOpacity);
    element.setAttribute('display', 'block');
  }

  static addRotatingOuterRing({ action = 'remove', element }) {
    // remove existing animations
    element
      .querySelectorAll('animate, animateTransform')
      .forEach(n => n.remove());

    if (action === 'remove') {
      element.setAttribute('display', 'none');
      return;
    }

    const cx = 24;
    const cy = 24;
    const radius = 20;

    const circumference = 2 * Math.PI * radius;
    // const segments = 1;
    // const segment = circumference / segments;

    // creates dash + gap pattern
    element.setAttribute(
      'stroke-dasharray',
      `${circumference * 0.8} ${circumference}`,
    );

    element.setAttribute('stroke-linecap', 'round');
    element.setAttribute('stroke-width', 3);

    // ---- rotation (beacon spin) ----
    const rotate = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'animateTransform',
    );

    rotate.setAttribute('attributeName', 'transform');
    rotate.setAttribute('type', 'rotate');
    rotate.setAttribute('from', `0 ${cx} ${cy}`);
    rotate.setAttribute('to', `360 ${cx} ${cy}`);
    rotate.setAttribute('dur', '2s');
    rotate.setAttribute('repeatCount', 'indefinite');

    element.appendChild(rotate);
    element.setAttribute('display', 'block');
  }

  static serviceProviderHighlightMarker(serviceProviderId, locationId) {
    const animationEffect = MapLib.pulseAnimationToMarker;
    const marker = document.getElementById(
      MapLib.serviceProviderGetMarkerId(serviceProviderId, locationId),
    );
    const outerCircle = marker?.getElementById('mapMarkerSelectionArea');
    if (outerCircle) {
      animationEffect({
        element: outerCircle,
        action: 'add',
      });
    }
  }

  static serviceProviderUnHighlightMarker(serviceProviderId, locationId) {
    const animationEffect = MapLib.pulseAnimationToMarker;
    const marker = document.getElementById(
      MapLib.serviceProviderGetMarkerId(serviceProviderId, locationId),
    );
    const outerCircle = marker?.getElementById('mapMarkerSelectionArea');
    if (outerCircle) {
      animationEffect({
        element: outerCircle,
        action: 'remove',
      });
    }
  }

  serviceProviderUnHighlightAllMarkers(ignoreServiceProviders = []) {
    this.serviceProviders.every(sp => {
      if (!ignoreServiceProviders.includes(sp.id)) {
        sp.locations.forEach(location => {
          MapLib.serviceProviderUnHighlightMarker(sp.id, location.id);
        });
      }
      return true;
    });
  }

  async serviceProviderApplyToMapAsMarkers() {
    await this.serviceProviders.forEach(async (sp, index) => {
      sp?.locations.forEach(async location => {
        let markerElement = null;

        markerElement = ServiceProvider({
          onClickHandler: this.onServiceProviderClick,
          serviceProvider: sp,
          MapLib,
          location,
          theme: this.theme,
          index,
          type: this.isAssignedOrAcceptedSP(sp, location)
            ? 'ASSIGNED_OR_ACCEPTED'
            : 'GENERIC',
        });

        markerElement.style.cursor = 'pointer';

        this.marker.serviceProviders.push(
          new Marker({
            element: markerElement,
          })
            .setLngLat([location?.address?.lng, location.address?.lat])
            .addTo(this.map),
        );
      });
      // Apply the assigned jobs only once for each SP, assigned jobs are not by addresses as yet
      await this.serviceProviderServiceJobApplyToMapAsLayer({
        serviceProvider: sp,
        index,
      });
    });
    this.adjustMap();
  }

  static serviceProviderServiceJobGetLayerName(index) {
    return `spServiceJobLayer${index}`;
  }

  serviceProviderServiceJobIsHighlightedLayer(layerName) {
    return (
      this.map.getPaintProperty(layerName, 'icon-opacity') ===
      this.highlightedMarkerOpacity
    );
  }

  serviceProviderServiceJobHighlightLayer(layerName) {
    this.map.setPaintProperty(
      layerName,
      'icon-opacity',
      this.highlightedMarkerOpacity,
    );
  }

  serviceProviderServiceJobUnHighlightLayer(layerName) {
    this.map.setPaintProperty(
      layerName,
      'icon-opacity',
      this.unHighlightedMarkerOpacity,
    );
  }

  serviceProviderServiceJobUnHighlightAllLayers(ignoreServiceProviders) {
    this.serviceProviders.every((sp, index) => {
      if (!ignoreServiceProviders.includes(sp.id)) {
        this.serviceProviderServiceJobUnHighlightLayer(
          MapLib.serviceProviderServiceJobGetLayerName(index),
        );
      }
      return true;
    });
  }

  async serviceProviderServiceJobApplyToMapAsLayer({ index, serviceProvider }) {
    const SOURCE_NAME = `serviceProviderSource${index}`;
    const MARKER_NAME = `serviceProviderMarkers${index}`;
    const LAYER_ID = MapLib.serviceProviderServiceJobGetLayerName(index);

    const features = MapLib.serviceProviderServiceJobGetFeatures({
      index,
      layerId: LAYER_ID,
      serviceProvider,
    });

    const source = MapLib.coreCreateSource({
      sourceName: SOURCE_NAME,
      features: features || [],
    });

    if (!this.map.hasImage(MARKER_NAME)) {
      const size = 28;
      const svgMarker = SPExistingServiceJobMarker({
        text: index + 1,
        size,
      });

      const svgImage = new Image(size, size);
      svgImage.src = MapLib.coreSvgObjectToImageSrc(svgMarker);
      svgImage.onload = () => {
        this.map.addImage(MARKER_NAME, svgImage);
      };
    } else {
      this.map.removeLayer(LAYER_ID);
      this.map.removeSource(SOURCE_NAME);
    }

    this.map.addSource(SOURCE_NAME, source);

    this.map.addLayer(
      MapLib.coreCreateLayer({
        layerId: LAYER_ID,
        sourceName: SOURCE_NAME,
        markerName: MARKER_NAME,
      }),
    );

    return this.map;
  }

  static serviceProviderServiceJobGetFeatures({
    serviceProvider,
    index,
    layerId,
  }) {
    const features = serviceProvider?.locations.reduce((acc, location) => {
      const currentFeatures = location?.assignedServiceJobs?.map(
        assignedServiceJob => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [
              assignedServiceJob.locationLng,
              assignedServiceJob.locationLat,
            ],
          },
          properties: {
            serviceProviderId: serviceProvider.id,
            locationID: location?.id,
            index,
            layerId,
          },
        }),
      );
      return [...acc, ...currentFeatures];
    }, []);
    return features;
  }

  serviceProviderServiceJobOnClick(e) {
    const { serviceProviderId, layerId } = e.features[0].properties;
    const serviceProviders = MapLib.jobInfo?.current?.serviceProviders;
    // Deselect any previous layers
    this.serviceProviderUnHighlightAllMarkers([serviceProviderId]);
    this.serviceProviderServiceJobUnHighlightAllLayers(serviceProviders, [
      serviceProviderId,
    ]);
    // Select the clicked layer
    if (this.serviceProviderServiceJobIsHighlightedLayer(layerId)) {
      MapLib.serviceProviderUnHighlightMarker(serviceProviderId);
      this.serviceProviderServiceJobUnHighlightLayer(layerId);
    } else {
      MapLib.serviceProviderHighlightMarker(serviceProviderId);
      this.serviceProviderServiceJobHighlightLayer(layerId);
    }
  }

  /** Below code is unused but might be needed when we have a ton of SP to be displayed */
  /** ------------------------------------ start ------------------------------------ */
  async serviceProviderApplyToMapAsLayer({ serviceProviders, markerUrl }) {
    const SOURCE_NAME = 'serviceProviderSource';
    const MARKER_NAME = 'serviceProviderMarkers';
    const LAYER_ID = 'serviceProvidersLayer';

    const features = this.serviceProviderGetFeatures({ serviceProviders });

    const source = this.coreCreateSource({
      sourceName: SOURCE_NAME,
      features,
    });

    if (!this.map.hasImage(MARKER_NAME)) {
      const image = await this.map.loadImage(markerUrl);
      this.map.addImage(MARKER_NAME, image.data);
    } else {
      this.map.removeLayer(LAYER_ID);
      this.map.removeSource(SOURCE_NAME);
    }

    this.map.addSource(SOURCE_NAME, source);

    this.map.addLayer(
      this.coreCreateLayer({
        layerId: LAYER_ID,
        sourceName: SOURCE_NAME,
        markerName: MARKER_NAME,
      }),
    );

    return this.map;
  }

  serviceProviderGetFeatures() {
    return this.serviceProviders?.map(serviceProvider => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [serviceProvider.locationLng, serviceProvider.locationLng],
      },
      properties: {
        serviceProviderId: serviceProvider.id,
      },
    }));
  }
  /** ------------------------------------ end ------------------------------------ */
}

export default MapLib;
